import {priority} from '@priority-software/priority-proto-api/lib';
import {BatchRequest} from '../../services/proto/core/batchRequest';
import {batchKeys} from '../../services/proto/core/constants';
import {fetchColumns, fetchCount, fetchFormData, startForm} from '../../services/form/form';
import {
    DEFAULT_OPTIONAL_FIELDS,
    FAMILY_FORM_NAME,
    FETCH_BULK_TOP,
    PART_INVENTORY_FIELDS,
    PART_INVENTORY_SUBFORM_NAME,
    SELECTED_FIELDS_STORE_KEY,
    INACTIVE_FAMILY_FIELD_NAME,
    FAMILY_FIELD_NAME,
    FAMILY_DESC_NAME,
    FAMILY_EDESC_NAME
} from './consts';
import {getAdditionalProps} from '../../services/proto/core/loginProps';
import {CachedFields} from '../../stores/Form/FormsCache';
import {getLang} from '../../Localization';
import {getSelectedFieldsData, updateSelectedFieldsData} from '../../services/serverStoreData';

export const fetchPartsColumns = async (session: string) => {
    const res = await fetchColumns(session, {keepFocus: true});
    return res?.Columns;
};

export const fetchParts = async (
    session: string,
    page: number = 0,
    filters: priority.netitems.form.IFilter[],
    select?: number[]
) => {
    const res = await fetchFormData({
        select,
        session,
        options: {keepFocus: true},
        filters,
        filtersOperator: priority.netitems.shared.LogicalOperator.And,
        top: FETCH_BULK_TOP,
        skip: page * FETCH_BULK_TOP,
        attachments: true
    });

    return res.Records;
};
export const fetchInventoryColumns = async (session: string, Keys) => {
    const batchRequest = new BatchRequest(session);
    batchRequest.addTask({
        key: batchKeys.Subform,
        request: new priority.netitems.form.SubformRq({
            Session: session,
            Keys,
            Fname: PART_INVENTORY_SUBFORM_NAME,
            Properties: getAdditionalProps()
        })
    });

    batchRequest.addTask({
        key: batchKeys.Columns,
        request: new priority.netitems.form.ColumnsRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        cacheData: {key: CachedFields.columns, formName: PART_INVENTORY_SUBFORM_NAME}
    });

    batchRequest.addTask({
        key: batchKeys.End,
        request: new priority.netitems.form.EndRq({
            Session: session,
            Properties: getAdditionalProps()
        })
    });

    const batchRes = await batchRequest.getData({keepFocus: true});
    const columns = batchRes[1]?.Columns?.Columns?.filter(col =>
        PART_INVENTORY_FIELDS.includes(col.Name)
    );
    return columns || [];
};

export const getSelectedFields = async (form: string) => {
    const key = `${SELECTED_FIELDS_STORE_KEY}_${getLang()}_${form}`;

    return await getSelectedFieldsData(key, DEFAULT_OPTIONAL_FIELDS);
};

export const updateSelectedFields = async (
    selectedFields: any[],
    form: string,
    shouldShowQuantitySelector: boolean
) => {
    const key = `${SELECTED_FIELDS_STORE_KEY}_${getLang()}_${form}`;

    return await updateSelectedFieldsData(selectedFields, shouldShowQuantitySelector, key);
};

export const fetchFamilyOptions = async () => {
    const formData = await startForm(FAMILY_FORM_NAME, {keepFocus: true, handleMessages: true});

    const session = formData?.Session;
    if (session) {
        const res = await fetchColumns(session, {keepFocus: true, handleMessages: true});
        if (res?.Columns) {
            const columns = res?.Columns;
            const columnsIdsFamilyField = columns?.reduce((acc, col) => {
                if (
                    col.Name === FAMILY_FIELD_NAME ||
                    col.Name === FAMILY_DESC_NAME ||
                    col.Name === FAMILY_EDESC_NAME
                ) {
                    acc.push(col.ID);
                }
                return acc;
            }, []);
            const inactiveColId = columns?.find(col => col.Name === INACTIVE_FAMILY_FIELD_NAME).ID;
            const batchRequest = new BatchRequest(session);

            const filters: priority.netitems.form.IFilter[] = [
                {
                    Condition: [
                        {
                            ColumnID: inactiveColId,
                            Operator: priority.netitems.shared.ComparisonOperator.Eq,
                            Values: ['']
                        }
                    ]
                }
            ];

            batchRequest.addTask({
                key: batchKeys.Fetch,
                request: new priority.netitems.form.FetchRq({
                    Select: columnsIdsFamilyField,
                    Session: session,
                    Filters: filters,
                    Properties: getAdditionalProps(),
                    Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
                })
            });

            batchRequest.addTask({
                key: batchKeys.End,
                request: new priority.netitems.form.EndRq({
                    Session: session,
                    Properties: getAdditionalProps()
                })
            });
            const batchRes = await batchRequest.getData({keepFocus: true});
            const records = batchRes[0]?.Fetch?.Records;

            return {columns, records};
        }
    }
    return null;
};

export const fetchPagesCount = async (
    session: string,
    filters: priority.netitems.form.IFilter[] = []
) => {
    const res = await fetchCount(session, filters, priority.netitems.shared.LogicalOperator.And, {
        keepFocus: true
    });
    return Math.ceil(Number(res.recordCount) / FETCH_BULK_TOP);
};

export const fetchInventoryData = async (
    session: string,
    keys: priority.netitems.form.IField[]
) => {
    const batchRequest = new BatchRequest(session);
    batchRequest.addTask({
        key: batchKeys.Subform,
        request: new priority.netitems.form.SubformRq({
            Session: session,
            Keys: keys,
            Fname: PART_INVENTORY_SUBFORM_NAME,
            Properties: getAdditionalProps()
        })
    });

    batchRequest.addTask({
        key: batchKeys.Columns,
        request: new priority.netitems.form.ColumnsRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        cacheData: {key: CachedFields.columns, formName: PART_INVENTORY_SUBFORM_NAME}
    });

    batchRequest.addTask({
        key: batchKeys.Fetch,
        request: new priority.netitems.form.FetchRq({
            Session: session,
            Properties: getAdditionalProps(),
            Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
        })
    });

    batchRequest.addTask({
        key: batchKeys.End,
        request: new priority.netitems.form.EndRq({
            Session: session,
            Properties: getAdditionalProps()
        })
    });

    const batchRes = await batchRequest.getData({keepFocus: true});

    return {records: batchRes[2]?.Fetch?.Records, columns: batchRes[1]?.Columns?.Columns};
};
